/**
 * The sync engine: drains the outbox, then pulls what changed.
 *
 * Nothing in the UI calls this and waits. It runs in the background and the
 * screens simply re-render when Dexie changes underneath them.
 *
 * Ordering is push-then-pull, and that order matters. Pulling first would
 * overwrite local rows with server versions that don't yet include the edits
 * still sitting in the outbox, so the user would watch their last set flicker
 * away and come back.
 */

import { api, ApiError, getToken } from '../api/client'
import { CURSOR_KEY, db, getMeta, setMeta, type Metric, type SetRow, type Session } from '../db'
import { isOnline, watch as watchNetwork } from '../native/network'

const PUSH_BATCH = 200

export type SyncState = {
  running: boolean
  online: boolean
  pending: number
  lastSyncedAt: number | null
  lastError: string | null
}

type Listener = (s: SyncState) => void

let state: SyncState = {
  running: false,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  pending: 0,
  lastSyncedAt: null,
  lastError: null,
}

const listeners = new Set<Listener>()

export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  fn(state)
  return () => listeners.delete(fn)
}

function emit(patch: Partial<SyncState>) {
  state = { ...state, ...patch }
  listeners.forEach((fn) => fn(state))
}

async function refreshPending() {
  emit({ pending: await db.outbox.count() })
}

// --- push -------------------------------------------------------------------

type PushResponse = {
  applied: string[]
  skipped: string[]
  resolved: { op_id: string; entity: string; row: Record<string, unknown> }[]
  errors?: { op_id: string | null; error: string }[]
  server_cursor: number
  prs?: { exercise_id: number; kinds: string[] }[]
}

async function pushOnce(): Promise<{ moved: boolean; prs: PushResponse['prs'] }> {
  const ops = await db.outbox.orderBy('created_at').limit(PUSH_BATCH).toArray()
  if (ops.length === 0) return { moved: false, prs: [] }

  const res = await api<PushResponse>('/sync/push', {
    body: {
      ops: ops.map((o) => ({
        op_id: o.op_id,
        entity: o.entity,
        op: o.op,
        payload: o.payload,
      })),
    },
  })

  // `applied` and `skipped` are both settled: skipped means the server had
  // already applied this op_id in an earlier attempt whose response we never
  // saw. Treating skipped as failure is what turns one flaky response into an
  // outbox that never drains.
  const done = new Set([...(res.applied ?? []), ...(res.skipped ?? [])])

  // The server kept its own version because ours was stale. Overwrite locally —
  // silently, because the alternative is a conflict dialog for a case that in
  // practice means "this phone's clock is wrong" or "I edited this on the
  // laptop yesterday".
  for (const r of res.resolved ?? []) {
    done.add(r.op_id)
    await applyServerRow(r.entity, r.row)
  }

  // A rejected op (malformed, or targeting someone else's row) will never
  // succeed on retry. Drop it, but keep the reason visible rather than failing
  // silently forever.
  for (const e of res.errors ?? []) {
    if (e.op_id) {
      done.add(e.op_id)
      console.warn('[sync] op rejected, dropping:', e.op_id, e.error)
    }
  }

  await db.outbox.bulkDelete([...done])
  await refreshPending()

  return { moved: done.size > 0, prs: res.prs ?? [] }
}

// --- pull -------------------------------------------------------------------

type PullResponse = {
  sessions: Session[]
  sets: SetRow[]
  metrics: Metric[]
  next_cursor: number
  has_more: boolean
}

/**
 * Write a server row into Dexie unless the row has unsynced local changes.
 *
 * Without the dirty check, a pull landing between a local edit and its push
 * would overwrite the edit with the server's older copy — the user's set
 * reverts on screen for no visible reason. The outbox is the source of truth
 * for "this row is mid-flight".
 */
async function applyServerRow(entity: string, row: Record<string, unknown>) {
  const id = row.id as string
  const pending = await db.outbox.where('row_id').equals(id).count()
  if (pending > 0) return

  if (entity === 'session') await db.sessions.put(row as unknown as Session)
  else if (entity === 'set') await db.sets.put(row as unknown as SetRow)
  else if (entity === 'metric') await db.metrics.put(row as unknown as Metric)
}

async function pullOnce(): Promise<boolean> {
  const cursor = await getMeta<number>(CURSOR_KEY, 0)
  const res = await api<PullResponse>(`/sync/pull?since=${cursor}`)

  const rows =
    res.sessions.length + res.sets.length + res.metrics.length

  if (rows > 0) {
    for (const s of res.sessions) await applyServerRow('session', s as never)
    for (const s of res.sets) await applyServerRow('set', s as never)
    for (const m of res.metrics) await applyServerRow('metric', m as never)
  }

  // Advance the cursor even when a batch contained only rows we skipped as
  // dirty — those rows are about to be pushed anyway, and not advancing would
  // re-fetch them forever.
  if (res.next_cursor > cursor) await setMeta(CURSOR_KEY, res.next_cursor)

  return res.has_more
}

// --- reference data ---------------------------------------------------------

/**
 * Exercises are server-authored and change rarely, so they ride a plain REST
 * call rather than the seq cursor. Cached in Dexie so the picker works offline.
 */
export async function refreshExercises(): Promise<void> {
  const res = await api<{ exercises: unknown[] }>('/exercises')
  await db.transaction('rw', db.exercises, async () => {
    await db.exercises.clear()
    await db.exercises.bulkPut(res.exercises as never[])
  })
}

/**
 * Plans, same deal — server-authored, read-only here, cached so a planned
 * workout can be started with no signal.
 *
 * Clear-then-put rather than a merge: the server is the only writer, so a plan
 * that has been archived there should disappear here too. A merge would leave
 * archived plans on the device forever, offering workouts that no longer exist.
 */
export async function refreshPlans(prefetched?: unknown[]): Promise<void> {
  // The Plan screen already has this list in hand after its own fetch; letting
  // it hand the rows over saves a duplicate round trip on every visit.
  const plans = prefetched ?? (await api<{ plans: unknown[] }>('/plans')).plans
  await db.transaction('rw', db.plans, async () => {
    await db.plans.clear()
    await db.plans.bulkPut(plans as never[])
  })
}

// --- the loop ---------------------------------------------------------------

let inFlight: Promise<void> | null = null

export async function sync(opts: { reference?: boolean } = {}): Promise<void> {
  if (!getToken()) return
  // Collapse concurrent callers (a mutation debounce firing while the interval
  // tick is already running) onto one in-flight run.
  if (inFlight) return inFlight

  inFlight = (async () => {
    emit({ running: true, lastError: null })
    try {
      // Drain fully — a long offline stretch can queue more than one batch.
      for (let i = 0; i < 20; i++) {
        const { moved } = await pushOnce()
        if (!moved) break
      }

      for (let i = 0; i < 50; i++) {
        const more = await pullOnce()
        if (!more) break
      }

      if (opts.reference) {
        await refreshExercises()
        await refreshPlans()
      }

      emit({ lastSyncedAt: Date.now(), online: true })
    } catch (err) {
      // 401 is terminal, not transient: api/client.ts has already cleared the
      // token, and the app will drop to the sign-in screen. Retrying would just
      // spin.
      if (err instanceof ApiError && err.status === 401) {
        emit({ lastError: 'Signed out' })
      } else {
        emit({ lastError: err instanceof Error ? err.message : 'Sync failed' })
        // Asked rather than assumed: a failed request is not proof of being
        // offline (the server could be down), and on Android only the Network
        // plugin knows the difference.
        void isOnline().then((online) => emit({ online }))
      }
    } finally {
      await refreshPending()
      emit({ running: false })
      inFlight = null
    }
  })()

  return inFlight
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

/** Call after a mutation. Coalesces a burst of set-logging into one request. */
export function syncSoon(delayMs = 3000): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    void sync()
  }, delayMs)
}

let started = false

/**
 * Wire up the triggers. Called once, after sign-in.
 *
 * There is deliberately no background sync service. Android kills them, and the
 * value is near zero: the data is already safe on the phone, and it reaches the
 * server the next time the app is opened.
 */
export function startSyncLoop(): () => void {
  if (started) return () => {}
  started = true

  const onVisible = () => {
    if (document.visibilityState === 'visible') void sync()
  }

  const unwatch = watchNetwork((online) => {
    emit({ online })
    if (online) void sync()
  })

  // The subscription only reports changes, so seed the current state.
  void isOnline().then((online) => emit({ online }))

  document.addEventListener('visibilitychange', onVisible)

  // Slow poll while foregrounded, to pick up another device's writes.
  const interval = setInterval(() => {
    if (document.visibilityState === 'visible') void sync()
  }, 5 * 60 * 1000)

  void sync({ reference: true })

  return () => {
    started = false
    unwatch()
    document.removeEventListener('visibilitychange', onVisible)
    clearInterval(interval)
  }
}
